import { Mesh, Program, Renderer, Texture, Triangle } from "ogl";

import { logger } from "@utilities/logger";

import RAF from "@ui/components/core/Orb/glxp/utils/RAF";

import { GLSL_GET_COVER_UV, GLSL_QUADRATIC_OUT, RING_TRANSITION_DURATION, RING_VERTEX } from "./ringShaderChunks";

/**
 * A WebGL2 ripple-reveal transition between two images, adapted from the dubbing
 * `RingTransitionFX` (PR #3612). A soft ring expands from a centre point: inside the
 * ring the *incoming* image is revealed, outside the *outgoing* one stays, and the
 * ring edge displaces both radially with a brief brightness pulse. Used to replace
 * the opacity crossfade between two images of the same scenario in InteractiveImage.
 *
 * The canvas only renders while a transition is in flight (subscribes to the shared
 * RAF for ~1s, then unsubscribes); when idle the host shows the settled image itself.
 * Falls back gracefully (`isSupported === false`) when WebGL2 is unavailable.
 */

const FRAGMENT = /* glsl */ `#version 300 es
precision highp float;
in vec2 vUv;

uniform sampler2D uFrom;
uniform sampler2D uTo;
uniform vec2 uResolution;
uniform vec2 uFromResolution;
uniform vec2 uToResolution;
uniform float uProgress;
uniform vec2 uRingCenter;

out vec4 outColor;
${GLSL_GET_COVER_UV}
${GLSL_QUADRATIC_OUT}
// Fade the radial displacement to zero near the sampled texture's [0,1] borders. On an
// axis fit exactly to the frame (no cover headroom) an outward displacement would push the
// sample past the edge, and CLAMP_TO_EDGE would smear that border column as the ring
// sweeps across it. Damping near the border keeps the lens distortion in the interior only.
vec2 dampedSample(vec2 sampleUv, vec2 disp) {
    vec2 edge = smoothstep(0.0, 0.08, sampleUv) * (1.0 - smoothstep(0.92, 1.0, sampleUv));
    return sampleUv + disp * min(edge.x, edge.y);
}

void main() {
    vec2 uv = vec2(vUv.x, 1.0 - vUv.y);
    float p = quadraticOut(uProgress);

    vec2 center = vec2(uRingCenter.x, 1.0 - uRingCenter.y);
    vec2 centered = uv - center;
    centered.x *= uResolution.x / uResolution.y;
    float dist = length(centered);

    // Wide enough that the ring still reaches every corner when the centre sits near the
    // bottom edge (the prompt bar), not just from the middle of the frame.
    float radius = p * 1.6;
    float ring = smoothstep(0.35, 0.0, abs(dist - radius)) * (1.0 - p);

    // Radial displacement around the ring edge (carried over from the PR effect).
    vec2 fromCenter = uv - center;
    float distFromCenter = length(fromCenter);
    vec2 normal = fromCenter / max(distFromCenter, 1e-4);
    float centerDamp = smoothstep(0.0, 0.2, distFromCenter);
    vec2 disp = normal * ring * 0.05 * centerDamp;

    vec3 fromColor = texture(uFrom, dampedSample(getCoverUv(uv, uResolution, uFromResolution), disp)).rgb;
    vec3 toColor = texture(uTo, dampedSample(getCoverUv(uv, uResolution, uToResolution), disp)).rgb;

    // Reveal the incoming image inside the expanding ring with a soft edge.
    float reveal = smoothstep(radius + 0.05, radius - 0.05, dist);
    vec3 color = mix(fromColor, toColor, reveal);

    // Bright pulse along the ring (carried over from the PR effect).
    color += vec3(ring * ring * 0.35 * (1.0 - p));

    outColor = vec4(color, 1.0);
}
`;

interface ImageRippleFXOptions {
  /** Fires when the first frame of a triggered transition has rendered (progress ≈ 0). */
  onStart?: () => void;
  /** Fires when a transition reaches progress 1. */
  onDone?: () => void;
  /** Fires when a transition's images fail to load, so the caller can fall back. */
  onError?: () => void;
}

class ImageRippleFX {
  isSupported = false;

  private container: HTMLElement;
  private dpr: number;
  private rafId: string;
  private onStart: (() => void) | null;
  private onDone: (() => void) | null;
  private onError: (() => void) | null;

  private renderer: Renderer | null = null;
  private program: Program | null = null;
  private mesh: Mesh | null = null;
  private fromTexture: Texture | null = null;
  private toTexture: Texture | null = null;

  private time = 0;
  private transitionStart: number | null = null;
  private startedCurrent = false;
  private isPlaying = false;
  private loadToken = 0;
  private cache = new Map<string, HTMLImageElement>();

  private resizeObserver: ResizeObserver | null = null;

  constructor(container: HTMLElement, options: ImageRippleFXOptions = {}) {
    this.container = container;
    this.dpr = Math.min(window.devicePixelRatio, 2);
    this.rafId = `image-ripple-fx-${Math.random().toString(36).slice(2)}`;
    this.onStart = options.onStart ?? null;
    this.onDone = options.onDone ?? null;
    this.onError = options.onError ?? null;

    try {
      this.init();
      this.isSupported = true;
    } catch (error) {
      logger.warn("[ImageRippleFX] WebGL2 init failed, falling back to native images", error);
      this.dispose();
    }
  }

  private init() {
    const { offsetWidth: w, offsetHeight: h } = this.container;

    this.renderer = new Renderer({
      dpr: this.dpr,
      width: w,
      height: h,
      alpha: true,
      premultipliedAlpha: true,
      antialias: false,
      powerPreference: "high-performance",
    });

    const gl = this.renderer.gl;
    const canvas = gl.canvas as HTMLCanvasElement;
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.display = "block";
    this.container.appendChild(canvas);

    const textureOptions = {
      generateMipmaps: false,
      minFilter: gl.LINEAR,
      magFilter: gl.LINEAR,
      wrapS: gl.CLAMP_TO_EDGE,
      wrapT: gl.CLAMP_TO_EDGE,
    } as const;
    this.fromTexture = new Texture(gl, textureOptions);
    this.toTexture = new Texture(gl, textureOptions);

    this.program = new Program(gl, {
      vertex: RING_VERTEX,
      fragment: FRAGMENT,
      uniforms: {
        uFrom: { value: this.fromTexture },
        uTo: { value: this.toTexture },
        uProgress: { value: 1 },
        uRingCenter: { value: [0.5, 0.5] },
        uResolution: { value: [w * this.dpr, h * this.dpr] },
        uFromResolution: { value: [1, 1] },
        uToResolution: { value: [1, 1] },
      },
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });

    this.mesh = new Mesh(gl, { geometry: new Triangle(gl), program: this.program });

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.container);
  }

  /** Ripple from `fromUrl` to `toUrl`; the ring expands from `centerUv` (defaults to centre). */
  transition(fromUrl: string, toUrl: string, centerUv: [number, number] = [0.5, 0.5]) {
    if (!this.isSupported || !this.program) return;
    const token = ++this.loadToken;

    Promise.all([this.load(fromUrl), this.load(toUrl)])
      .then(([fromImage, toImage]) => {
        // A newer transition (or disposal) superseded this load — drop it.
        if (token !== this.loadToken || !this.program || !this.fromTexture || !this.toTexture) return;

        this.fromTexture.image = fromImage;
        this.fromTexture.needsUpdate = true;
        this.toTexture.image = toImage;
        this.toTexture.needsUpdate = true;
        this.program.uniforms.uFromResolution.value = [fromImage.naturalWidth || 1, fromImage.naturalHeight || 1];
        this.program.uniforms.uToResolution.value = [toImage.naturalWidth || 1, toImage.naturalHeight || 1];
        this.program.uniforms.uRingCenter.value = [centerUv[0], centerUv[1]];
        this.program.uniforms.uProgress.value = 0;

        this.transitionStart = this.time;
        this.startedCurrent = false;
        this.play();
      })
      .catch(error => {
        // Ignore failures from a superseded transition (or after disposal).
        if (token !== this.loadToken) return;
        logger.warn("[ImageRippleFX] image load failed, skipping ripple", error);
        this.onError?.();
      });
  }

  private load(url: string) {
    const cached = this.cache.get(url);
    if (cached?.complete && cached.naturalWidth > 0) return Promise.resolve(cached);
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.onload = () => {
        this.cache.set(url, image);
        resolve(image);
      };
      image.onerror = reject;
      image.src = url;
    });
  }

  private play() {
    if (this.isPlaying || !this.isSupported) return;
    this.isPlaying = true;
    RAF.subscribe(this.rafId, (dt: number) => this.update(dt / 1000));
  }

  private stop() {
    if (!this.isPlaying) return;
    this.isPlaying = false;
    RAF.unsubscribe(this.rafId);
  }

  private resize() {
    if (!this.renderer || !this.program) return;
    const { offsetWidth: w, offsetHeight: h } = this.container;
    this.renderer.setSize(w, h);
    this.program.uniforms.uResolution.value = [w * this.dpr, h * this.dpr];
  }

  private update(dt: number) {
    if (!this.renderer || !this.program || !this.mesh || this.transitionStart === null) return;

    this.time += dt;
    const progress = Math.min((this.time - this.transitionStart) / RING_TRANSITION_DURATION, 1);
    this.program.uniforms.uProgress.value = progress;
    this.renderer.render({ scene: this.mesh });

    if (!this.startedCurrent) {
      this.startedCurrent = true;
      this.onStart?.();
    }

    if (progress >= 1) {
      this.transitionStart = null;
      this.stop();
      this.onDone?.();
    }
  }

  dispose() {
    this.stop();
    this.loadToken++;
    this.onStart = null;
    this.onDone = null;
    this.onError = null;
    this.resizeObserver?.disconnect();

    const canvas = this.renderer?.gl.canvas as HTMLCanvasElement | undefined;
    canvas?.parentNode?.removeChild(canvas);

    this.renderer = null;
    this.program = null;
    this.mesh = null;
    this.fromTexture = null;
    this.toTexture = null;
    this.resizeObserver = null;
    this.cache.clear();
  }
}

export default ImageRippleFX;
